// AI-recipe pinned canonicals — the "+" button on a kitchen card that
// pre-stars an ingredient for the next AI Recipe generation.
//
// Storage shape: a single device-local array of canonical slugs in
// localStorage. NOT partitioned by userId — that caused a race where
// the KitchenCard mounted before useAuth resolved (userId=null →
// "anon" key), the user clicked "+" which wrote to "anon", and then
// auth landed with the real user id, re-fired the effect, read from
// the user's key (empty), and flipped pinned back to false. Since
// pins are ephemeral (consumed on the next AI sketch) and per-device
// localStorage is already shared by every user on the machine, a
// global key is the right scope. Versioned so a future schema change
// can drop incompatible older pins rather than silently surface stale
// state. Pins also auto-expire after MAX_AGE_MS so a forgotten
// pinning session from weeks ago doesn't poison a fresh AI run.
//
// Cross-component updates go through a custom event
// (`aiRecipePinned:changed`) so multiple KitchenCards on screen flip
// their visual state in lockstep when any one of them is toggled.

const VERSION = 1;
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;
const EVENT_NAME = "aiRecipePinned:changed";
const STORAGE_KEY = `mise:ai-pinned:v${VERSION}`;

function readRaw() {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== VERSION) return null;
    const savedAt = parsed.savedAt ? new Date(parsed.savedAt).getTime() : 0;
    if (!savedAt || Date.now() - savedAt > MAX_AGE_MS) return null;
    return Array.isArray(parsed.ids) ? parsed.ids : [];
  } catch (_) {
    return null;
  }
}

function writeRaw(ids) {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    const payload = {
      v: VERSION,
      savedAt: new Date().toISOString(),
      ids: Array.isArray(ids) ? ids : [],
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    // Broadcast in-tab so other mounted KitchenCards re-read. The
    // built-in `storage` event fires across tabs only, so we use a
    // custom event for the single-tab case (the common one).
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
  } catch (_) { /* quota / private mode — non-fatal */ }
}

// All public APIs ignore any userId argument so existing call sites
// (which pass userId for historic reasons) keep working without a
// rewrite.
export function loadPinned() {
  return readRaw() || [];
}

export function isPinned(_userId, canonicalId) {
  // Accepts (canonicalId) OR (userId, canonicalId) — call sites passed
  // userId before; ignore it. The first form lets us drop userId from
  // hot paths cleanly.
  const id = canonicalId === undefined ? _userId : canonicalId;
  if (!id) return false;
  return (readRaw() || []).includes(id);
}

// Toggle a canonical in/out of the pin set. Returns the resulting
// array so callers don't need a follow-up read. Accepts the legacy
// (userId, canonicalId) signature for back-compat.
export function togglePinned(_userId, canonicalId) {
  const id = canonicalId === undefined ? _userId : canonicalId;
  if (!id) return readRaw() || [];
  const current = readRaw() || [];
  const next = current.includes(id)
    ? current.filter(x => x !== id)
    : [...current, id];
  writeRaw(next);
  return next;
}

// Drop the entire pin set — called by AIRecipe after a sketch
// generation consumes the pins (otherwise the same canonicals would
// re-pin on every subsequent AI session).
export function clearPinned() {
  if (typeof window === "undefined" || !window.localStorage) return;
  try { window.localStorage.removeItem(STORAGE_KEY); } catch (_) { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent(EVENT_NAME)); } catch (_) { /* ignore */ }
}

// Subscribe to pin-set changes. Returns a cleanup that removes the
// listeners. Fires on both same-tab toggles (custom event) and
// cross-tab toggles (storage event).
export function subscribePinned(handler) {
  if (typeof window === "undefined") return () => {};
  const onCustom = () => handler();
  const onStorage = (e) => {
    if (e?.key && e.key.startsWith(`mise:ai-pinned:v${VERSION}:`)) handler();
  };
  window.addEventListener(EVENT_NAME, onCustom);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(EVENT_NAME, onCustom);
    window.removeEventListener("storage", onStorage);
  };
}
